// held-target-move.ts — the page half of caster_may_move_target_each_turn_ft (2026-10-07; Psion Telekinetic "Cogni Puppet Strings" and
// "World in Motion" — janus-engine/movement/held-target-move.ts holds the rule).
//
// On the turn of a creature this page controls (movementPermission: its owner, or the GM), every creature holding a live status that
// creature applied from an item carrying the layer is OFFERED as a move: a small panel names the item, the held creature and the distance —
// "Choose destination" arms the FT3D teleport-style point picker centred on the held creature (its radius is the move), "Skip" spends the
// offer. The owner prefers prompting players over auto-triggers, so nothing moves on its own.
// ONCE PER TURN, on every page (owner 2026-10-08: the front end calculates from the encounter state it holds and broadcasts; the backend
// lazy-saves): a move or a skip puts the marker status "Telekinetic Hold Used" on the CASTER — Dodge's lane (action-truth.ts
// commitSelfMarkerStatus): applied to the row at once, every page hears it as an action-truth frame, saved under the caster's revision CAS
// (a second page acting in the same instant is answered stale and its marker reverted), gone at the start of the caster's next turn. The
// offer reads the caster's own status rows (heldTargetMoveSpentThisTurn) against the turn token every page computes alike.
// The pick is judged here (heldTargetMoveCheck: within N ft of the creature, within the item's range band from the caster) and then runs
// as a FORCE MOVE of the held creature (force-move-truth.ts tryForceMove through tryCommitPlainWalk with `heldMove`), sent once the marker's
// save is confirmed: the map Force Move's landing / zone / reach rules apply, the caster's permission stands in for the moved row's, and the
// receivers prove the hold from the status row (held_target_move_authority.php) — PHP never re-judges the distance or the cap. A move the
// receiver refuses gives the turn back: the marker is deleted through the status lane (status-truth.ts tryCommitStatusDelete).
import { encounterField } from '../shared/array-types';
import { actorEncounterFor, actorRowFor, movementPermission, movementScopeReaders, MovementRefused, tryCommitPlainWalk, hydrateMovementStatuses } from './movement-truth.js';
import { commitSelfMarkerStatus } from './action-truth.js';
import { tryCommitStatusDelete } from './status-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { HELD_TARGET_MOVE_MARKER_ID, heldTargetMoveCheck, heldTargetMoveOffers, heldTargetMoveSpendConnectedId, heldTargetMoveSpentThisTurn,
  heldTargetMoveTurnToken, type HeldTargetMoveOffer, type HeldTargetMoveReaders } from '../janus-engine/movement/held-target-move.js';
import { groundOccupancyBaseSizeFt } from '../shared/teleport-placement';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

/** A sent move's operation id → what releasing it needs (the caster's marker for that turn). */
const pendingOps = new Map<string, { dest: { x: number; y: number; z: number }; encounterId: string; casterId: string; connectedId: string }>();
/** A MovementTruthChange {x_pos,y_pos,z_pos}, {x,y,z} or [x,y,z] → a finite point, else null. */
function pointOf(value: unknown): { x: number; y: number; z: number } | null {
  const v = value as Record<string, unknown> | unknown[] | null | undefined;
  const raw = Array.isArray(v) ? [v[0], v[1], v[2]]
    : v && typeof v === 'object' ? ('x_pos' in v ? [v.x_pos, v.y_pos, v.z_pos] : [v.x, v.y, v.z]) : null;
  if (!raw) return null;
  const x = Number(raw[0]), y = Number(raw[1]), z = Number(raw[2]);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y, z: Number.isFinite(z) ? z : 0 } : null;
}
/** The caster's marker row for this turn as the page holds it (its saved id, once the Dodge lane stamped it). */
function markerRow(encounterId: string, casterId: string, connectedId: string): Row | null {
  const caster = actorRowFor(encounterId, casterId) as Row | null;
  const list = Array.isArray(asRow(caster?.statuses)?.target) ? (asRow(caster!.statuses)!.target as unknown[]) : [];
  return list.map(asRow).find((s): s is Row => !!s && upper(s.status_item_id) === HELD_TARGET_MOVE_MARKER_ID && upper(s.status_connected_id) === connectedId) ?? null;
}
/** Give the turn back: delete the caster's marker for it (the status lane; every page drops it). */
function releaseMarker(encounterId: string, casterId: string, connectedId: string): void {
  const row = markerRow(encounterId, casterId, connectedId);
  if (!row || !UUID.test(upper(row.status_id))) return;
  void tryCommitStatusDelete({ statusId: upper(row.status_id), scope: 'single' }).finally(() => { signature = ''; render(); });
}
/**
 * Give the turn's offer back when the move did not happen. A refusal always releases. A reconciled
 * (rejected / stale) answer releases only when the authoritative position is NOT the picked destination:
 * a retried commit whose first send already landed comes back stale, and releasing then would hand the
 * caster a second move in the same turn.
 */
function release(operationId: unknown, authoritativePosition?: unknown): void {
  const pending = pendingOps.get(String(operationId ?? ''));
  if (!pending) return;
  pendingOps.delete(String(operationId));
  const at = authoritativePosition === undefined ? null : pointOf(authoritativePosition);
  if (at && Math.hypot(at.x - pending.dest.x, at.y - pending.dest.y) <= 0.5) return; // it landed: the turn stays spent
  releaseMarker(pending.encounterId, pending.casterId, pending.connectedId);
}

let panel: HTMLElement | null = null;
let signature = '';
let busy = false;

interface TurnContext { encounterId: string; casterId: string; /** heldTargetMoveTurnToken — the same on every page */ turn: string; encounter: Row }
/** The viewed encounter's current turn, when this page may act for its holder. */
function turnContext(): TurnContext | null {
  const encounterId = upper(window.ftGetActiveEncounterId?.() ?? '');
  if (!UUID.test(encounterId)) return null;
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter) return null;
  if (Number(scalar(encounterField(encounter, 'in_combat')) ?? 0) !== 1) return null;
  const casterId = upper(encounterField(encounter, 'current_encounter_creature_id_turn'));
  if (!UUID.test(casterId) || !actorRowFor(encounterId, casterId)) return null;
  if (!movementPermission(encounterId, casterId).allowed) return null;
  return { encounterId, casterId, turn: heldTargetMoveTurnToken(key => encounterField(encounter, key)), encounter };
}

function readers(encounter: Row): HeldTargetMoveReaders {
  const scope = movementScopeReaders(encounter);
  // the applying item's definition: the encounter catalog / object cache, else a status row that embeds it (the item-as-status row)
  const embedded = (id: string): Row | null => {
    for (const row of (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) as unknown[]) {
      const creature = asRow(row);
      if (!creature) continue;
      for (const status of scope.statuses(creature)) {
        const def = asRow(status.status_arr);
        if (def && upper(asRow(def.items)?.item_id ?? status.status_item_id) === id && asRow(def.item_layer_types)) return def;
      }
    }
    return null;
  };
  return { value: (row, key) => scope.value(row, key), statuses: row => scope.statuses(row), position: row => scope.position(row),
    definition: id => scope.definition(id) ?? embedded(upper(id)) };
}

function removePanel(): void { panel?.remove(); panel = null; signature = ''; }

/** Has the caster spent its held move this turn, on any page? Its own status rows say (the marker). */
function spentThisTurn(context: TurnContext): boolean {
  const caster = actorRowFor(context.encounterId, context.casterId) as Row | null;
  return !!caster && heldTargetMoveSpentThisTurn(movementScopeReaders(context.encounter).statuses(caster), context.casterId, context.turn);
}

/** Every offer the rows allow, ignoring the spend (the post-marker re-derivation reads it this way). */
function rawOffers(context: TurnContext): HeldTargetMoveOffer[] {
  try { return heldTargetMoveOffers(context.encounter, context.casterId, readers(context.encounter)); }
  catch (error) { console.warn('[HELD-TARGET-MOVE]', error); return []; }
}
function currentOffers(context: TurnContext): HeldTargetMoveOffer[] {
  return spentThisTurn(context) ? [] : rawOffers(context);
}

/** The page's own check of a pick against the rows it holds: the held creature and its holder are here, and heldTargetMoveCheck passes. */
function heldMoveVerdict(context: TurnContext, offer: HeldTargetMoveOffer, point: { x: number; y: number; z: number }):
  { ok: true; from: [number, number, number] } | { ok: false; message: string } {
  const read = readers(context.encounter);
  const casterRow = actorRowFor(context.encounterId, context.casterId) as Row | null;
  const targetRow = actorRowFor(context.encounterId, offer.targetId) as Row | null;
  if (!casterRow || !targetRow) return { ok: false, message: `${offer.originName}: ${offer.targetName} is no longer on this page.` };
  const from = read.position(targetRow);
  const verdict = heldTargetMoveCheck(offer, read.position(casterRow), from, point);
  return verdict.ok ? { ok: true, from } : { ok: false, message: verdict.message };
}

/** Spend the turn: the marker on the caster (Dodge's lane). `accepted` runs once its save is confirmed; null = not sent. */
function spend(context: TurnContext, originName: string, accepted?: () => void): string | null {
  if (!markerWarm) { markerWarm = true; void fetchAndCacheObject(HELD_TARGET_MOVE_MARKER_ID).then(def => { if (!def) markerWarm = false; }, () => { markerWarm = false; }); }
  const connectedId = heldTargetMoveSpendConnectedId(context.casterId, context.turn);
  try {
    return commitSelfMarkerStatus({ encounterId: context.encounterId, actorId: context.casterId, markerId: HELD_TARGET_MOVE_MARKER_ID, connectedId,
      // (`revived` is not a spend signal: an expired marker is soft-deleted and the next turn's save REVIVES that row — the unique key)
      settled: { accepted: () => { busy = false; accepted?.(); signature = ''; render(); },
        // answered stale (another page acted on the caster first) or refused: its marker is reverted — nothing moves
        dropped: () => { busy = false; displayWarning(`${originName}: another action on this creature was saved first. Nothing was moved.`); signature = ''; render(); } } });
  } catch (error) {
    displayWarning(`${originName}: ${error instanceof Error ? error.message : String(error)}`);
    return null;
  }
}

function move(context: TurnContext, picked: HeldTargetMoveOffer, point: { x: number; y: number; z: number }): void {
  const live = turnContext();
  if (!live || live.encounterId !== context.encounterId || live.casterId !== context.casterId || live.turn !== context.turn) { render(); return; }
  if (spentThisTurn(live)) { displayWarning(`${picked.originName} was already used this turn.`); render(); return; }
  // re-derive the offer from the rows the page holds NOW (Codex review: a replaced granting row must be the one the zone check reads)
  const offer = rawOffers(live).find(o => o.originId === picked.originId && o.targetId === picked.targetId);
  if (!offer) { displayWarning(`${picked.originName}: ${picked.targetName} is no longer held.`); render(); return; }
  const precheck = heldMoveVerdict(live, offer, point);
  if (!precheck.ok) { displayWarning(precheck.message); return; }
  const connectedId = heldTargetMoveSpendConnectedId(live.casterId, live.turn);
  busy = true;
  // the marker first (shown at once, on every page); the move is sent once its save is confirmed — a page that lost the race never moves
  const sent = spend(live, offer.originName, () => { void moveHeldTarget(live, offer, point, connectedId); });
  if (sent === null) busy = false;
  else window.setTimeout(() => { busy = false; }, 20000);
  signature = ''; render();
}
async function moveHeldTarget(live: TurnContext, offer: HeldTargetMoveOffer, point: { x: number; y: number; z: number }, connectedId: string): Promise<void> {
  let operationId = '';
  const queued = (event: Event): void => { operationId = String((event as CustomEvent).detail?.command?.operation_id ?? ''); };
  try {
    await hydrateMovementStatuses(live.encounterId);
    const now = turnContext();
    const fresh = now && now.turn === live.turn && now.casterId === live.casterId
      ? rawOffers(now).find(o => o.originId === offer.originId && o.targetId === offer.targetId) ?? null : null;
    const still = now && fresh ? heldMoveVerdict(now, fresh, point) : null;
    if (!now || !fresh || !still || !still.ok) {
      releaseMarker(live.encounterId, live.casterId, connectedId);
      displayWarning(still && !still.ok ? still.message : fresh ? `${offer.originName}: the turn moved on.` : `${offer.originName}: ${offer.targetName} is no longer held.`);
      return;
    }
    window.addEventListener('ft:force-move-truth-queued', queued);
    const result = await tryCommitPlainWalk({ encounterId: now.encounterId, actorId: fresh.targetId, origin: { x: still.from[0], y: still.from[1], z: still.from[2] }, destination: point,
      route: [[still.from[0], still.from[1], still.from[2]], [point.x, point.y, point.z]], speed: undefined, inCombat: true, force: true, supportChain: [],
      heldMove: { casterId: fresh.casterId, originId: fresh.originId, statusIds: fresh.statusIds } });
    if (!result) throw new MovementRefused('Held movement could not be queued');
    if (operationId) {
      pendingOps.set(operationId, { dest: { x: point.x, y: point.y, z: point.z }, encounterId: now.encounterId, casterId: now.casterId, connectedId });
      if (pendingOps.size > 64) pendingOps.delete(pendingOps.keys().next().value!);
    }
    window.dispatchEvent(new CustomEvent('ft:held-target-move-queued', { detail: { offer: fresh, point } }));
  } catch (error) {
    releaseMarker(live.encounterId, live.casterId, connectedId);
    const message = error instanceof Error ? error.message : String(error);
    window.dispatchEvent(new CustomEvent('ft:held-target-move-refused', { detail: { offer, point, message } }));
    displayWarning(message);
  } finally {
    window.removeEventListener('ft:force-move-truth-queued', queued);
  }
}

/** The marker's definition in the page's object cache: the turn lane expires it at the caster's next turn start from the definition, and
 *  ANY page may run that boundary — warmed (once, one GET) as soon as a marker is seen on a creature in the viewed encounter. */
let markerWarm = false;
function warmMarkerDefinition(): void {
  if (markerWarm) return;
  const encounter = actorEncounterFor(upper(window.ftGetActiveEncounterId?.() ?? '')) as Row | null;
  const creatures = (Array.isArray(encounter?.encounter_creatures_arr) ? encounter!.encounter_creatures_arr : []) as unknown[];
  const seen = creatures.some(c => (Array.isArray(asRow(asRow(c)?.statuses)?.target) ? asRow(asRow(c)!.statuses)!.target as unknown[] : [])
    .some(s => upper(asRow(s)?.status_item_id) === HELD_TARGET_MOVE_MARKER_ID));
  if (!seen) return;
  markerWarm = true;
  void fetchAndCacheObject(HELD_TARGET_MOVE_MARKER_ID).then(def => { if (!def) markerWarm = false; }, () => { markerWarm = false; });
}

function render(): void {
  warmMarkerDefinition();
  const context = turnContext();
  const offers = context && !busy ? currentOffers(context) : [];
  const next = JSON.stringify(context ? [context.encounterId, context.casterId, context.turn, offers.map(o => [o.originId, o.targetId, o.ft])] : null);
  if (next === signature) return;
  removePanel(); signature = next;
  if (!context || !offers.length) return;
  panel = document.createElement('aside');
  panel.dataset.ftHeldTargetMove = '';
  panel.setAttribute('aria-label', 'Move a held creature');
  panel.style.cssText = 'position:fixed;right:16px;top:250px;z-index:20000;width:min(290px,calc(100vw - 32px));max-height:calc(100dvh - 280px);overflow:auto;padding:12px;background:#17232f;color:white;border:1px solid #d5bd77;border-radius:8px';
  const byOrigin = new Map<string, HeldTargetMoveOffer[]>();
  for (const offer of offers) byOrigin.set(offer.originId, [...(byOrigin.get(offer.originId) ?? []), offer]);
  for (const [originId, group] of byOrigin) {
    const block = document.createElement('div');
    block.className = 'mb-2';
    const title = document.createElement('p');
    title.className = 'mb-1';
    title.textContent = group.length > 1
      ? `${group[0]!.originName}: you may move one held creature up to ${group[0]!.ft} ft`
      : `${group[0]!.originName}: you may move ${group[0]!.targetName} up to ${group[0]!.ft} ft`;
    block.append(title);
    for (const offer of group) {
      const choose = document.createElement('button'); choose.type = 'button';
      choose.className = 'btn btn-sm btn-outline-light me-2 mb-1';
      choose.textContent = group.length > 1 ? `Move ${offer.targetName}` : 'Choose destination';
      choose.dataset.ftHeldTargetMoveChoose = `${offer.originId}|${offer.targetId}`;
      choose.addEventListener('click', () => {
        const target = actorRowFor(context.encounterId, offer.targetId) as Row | null;
        const sizeId = target ? ftEffectiveSizeId(target as never) ?? ftLayerValue(target, 'size') : null;
        // the picker measures its range from `casterId` — here the HELD creature, so the ring is the move itself
        // `controllerId`: the caster's dock shows Confirm and the shared preview is authorized for the caster (smoke 2026-10-07)
        window.ftStartTeleportPlacement?.({ casterId: offer.targetId, controllerId: offer.casterId, encounterId: context.encounterId, abilityId: offer.originId,
          name: `${offer.originName}: move ${offer.targetName}`, rangeFt: offer.ft, sizeFt: groundOccupancyBaseSizeFt(sizeId, SIZE_TYPES),
          cast: point => move(context, offer, { x: Number(point.x), y: Number(point.y), z: Number(point.z) }) });
      });
      block.append(choose);
    }
    const skip = document.createElement('button'); skip.type = 'button'; skip.textContent = 'Skip';
    skip.className = 'btn btn-sm btn-outline-light mb-1';
    skip.dataset.ftHeldTargetMoveSkip = originId;
    // a skip spends the turn the same way: the marker alone
    skip.addEventListener('click', () => { const live = turnContext(); if (live && live.turn === context.turn && !spentThisTurn(live)) spend(live, group[0]!.originName); signature = ''; render(); });
    block.append(skip);
    panel.append(block);
  }
  document.body.append(panel);
}

if (!(window as unknown as { __ftHeldTargetMoveMounted?: boolean }).__ftHeldTargetMoveMounted) {
  (window as unknown as { __ftHeldTargetMoveMounted?: boolean }).__ftHeldTargetMoveMounted = true;
  // the marker arrives on the caster's row through the action-truth / status frames like any status: re-evaluate on the row frames too
  for (const name of ['ft:encounter-turn-state', 'ft:encounter-state-received', 'ft:encounter-installed', 'ft:ws-state-frame', 'ft:action-truth-applied'])
    window.addEventListener(name, () => render());
  // statuses / rows arrive through many frames; a cheap local re-evaluation keeps the panel honest (no network)
  window.setInterval(render, 1500);
  // a refused (or stale-answered) move did not happen: the turn comes back (the marker is deleted); an accepted one is final
  window.addEventListener('ft:movement-truth-refused', event => release((event as CustomEvent).detail?.command?.operation_id));
  window.addEventListener('ft:movement-truth-reconciled', event => {
    const detail = (event as CustomEvent).detail;
    release(detail?.command?.operation_id, detail?.authoritative?.position ?? null);
  });
}
// Read-only diagnostics for smokes.
(window as unknown as { __ftHeldTargetMoveDebug?: () => unknown }).__ftHeldTargetMoveDebug = () => {
  const context = turnContext();
  return { context: context ? { encounterId: context.encounterId, casterId: context.casterId, turn: context.turn, round: context.turn } : null,
    offers: context ? currentOffers(context) : [], spent: context ? spentThisTurn(context) : null,
    marker: context ? markerRow(context.encounterId, context.casterId, heldTargetMoveSpendConnectedId(context.casterId, context.turn)) : null };
};
